/**
 * THE BUSINESS'S CLOCK (spec 2026-09-25, amended §2.2). A rules table, not
 * Intl: arithmetic that runs the same in Node, the browser and Hermes, and
 * that timezone.test.ts checks hour by hour against Node's full ICU. A zone
 * that ever changes its rules fails that test, not a customer's screen.
 */
export const TIMEZONES = {
    PA: [{ id: "America/Panama", label: "Panamá" }],
    CO: [{ id: "America/Bogota", label: "Colombia" }],
    CR: [{ id: "America/Costa_Rica", label: "Costa Rica" }],
    GT: [{ id: "America/Guatemala", label: "Guatemala" }],
    MX: [
        { id: "America/Mexico_City", label: "Centro (Ciudad de México)" },
        { id: "America/Cancun", label: "Sureste (Cancún)" },
        { id: "America/Mazatlan", label: "Pacífico (Mazatlán)" },
        { id: "America/Tijuana", label: "Noroeste (Tijuana)" },
    ],
    PE: [{ id: "America/Lima", label: "Perú" }],
    EC: [{ id: "America/Guayaquil", label: "Ecuador" }],
    UY: [{ id: "America/Montevideo", label: "Uruguay" }],
    VE: [{ id: "America/Caracas", label: "Venezuela" }],
    US: [
        { id: "America/New_York", label: "Este" },
        { id: "America/Chicago", label: "Centro" },
        { id: "America/Denver", label: "Montaña" },
        { id: "America/Phoenix", label: "Arizona" },
        { id: "America/Los_Angeles", label: "Pacífico" },
    ],
};
/** Standard offset in minutes east of UTC, and whether the zone follows the US summer rule. */
const RULES = {
    "America/Panama": { offset: -300, us: false },
    "America/Bogota": { offset: -300, us: false },
    "America/Costa_Rica": { offset: -360, us: false },
    "America/Guatemala": { offset: -360, us: false },
    "America/Mexico_City": { offset: -360, us: false }, // Mexico abolished DST in 2022, except the border
    "America/Cancun": { offset: -300, us: false },
    "America/Mazatlan": { offset: -420, us: false },
    "America/Tijuana": { offset: -480, us: true }, // the border follows the US
    "America/Lima": { offset: -300, us: false },
    "America/Guayaquil": { offset: -300, us: false },
    "America/Montevideo": { offset: -180, us: false },
    "America/Caracas": { offset: -240, us: false },
    "America/New_York": { offset: -300, us: true },
    "America/Chicago": { offset: -360, us: true },
    "America/Denver": { offset: -420, us: true },
    "America/Phoenix": { offset: -420, us: false }, // Arizona does not change
    "America/Los_Angeles": { offset: -480, us: true },
};
/** Day of month of the n-th Sunday of `month` (0-based), in UTC arithmetic. */
function nthSunday(year, month, n) {
    const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay(); // 0 = Sunday
    return 1 + ((7 - firstWeekday) % 7) + (n - 1) * 7;
}
/**
 * Minutes east of UTC in `tz` at the instant `at`. The US rule: one hour more
 * from 2:00 local standard time on the second Sunday of March until 2:00 local
 * daylight time on the first Sunday of November.
 */
export function offsetMinutesAt(tz, at) {
    const rule = RULES[tz];
    if (!rule.us)
        return rule.offset;
    const year = at.getUTCFullYear();
    const starts = Date.UTC(year, 2, nthSunday(year, 2, 2), 2) - rule.offset * 60_000;
    const ends = Date.UTC(year, 10, nthSunday(year, 10, 1), 2) - (rule.offset + 60) * 60_000;
    const t = at.getTime();
    return t >= starts && t < ends ? rule.offset + 60 : rule.offset;
}
export function defaultTimezone(country) {
    return TIMEZONES[country][0].id;
}
export function isTimezone(value) {
    return typeof value === "string" && Object.hasOwn(RULES, value);
}
export function timezoneBelongsTo(tz, country) {
    return TIMEZONES[country].some((zone) => zone.id === tz);
}
/**
 * Whether Configuración asks «Zona horaria» (spec 2026-09-25 §5): only where
 * the country has more than one -- a question with a single answer is not drawn.
 */
export function showsTimezone(country) {
    return TIMEZONES[country].length > 1;
}
/**
 * The zone as Configuración SHOWS it, with its offset now: «Panamá (UTC−5)»,
 * «Este (UTC−4)» in July. Every business sees its zone;
 * only Mexico and the US can change it. U+2212, not a hyphen: it is a sign.
 */
export function timezoneCaption(tz, country, at = new Date()) {
    const minutes = offsetMinutesAt(tz, at);
    const hours = Math.trunc(Math.abs(minutes) / 60);
    const rest = Math.abs(minutes) % 60;
    const offset = `UTC${minutes < 0 ? "−" : "+"}${hours}${rest === 0 ? "" : `:${String(rest).padStart(2, "0")}`}`;
    return `${timezoneLabel(tz, country)} (${offset})`;
}
/** The zone as Configuración names it -- «Centro», «Pacífico» -- from its country's own list. */
function timezoneLabel(tz, country) {
    return TIMEZONES[country].find((zone) => zone.id === tz)?.label ?? tz;
}
/**
 * THE ACTIVE ZONE, exactly like the active locale (locale.ts): a client sets it
 * the moment the business profile loads. THE SERVER NEVER CALLS THIS: it serves
 * every business at once and passes the zone explicitly (architecture.test.ts).
 */
let active = "America/Panama";
/**
 * Takes whatever the profile carries, and ignores a zone this build does not
 * know: the day a zone is added to the table and the
 * database, a bundle already on a phone would otherwise hand `offsetMinutesAt`
 * an id with no rule and fail on every date it draws. That bundle keeps its
 * current zone until the OTA brings the new rule.
 */
export function setTimezone(tz) {
    if (isTimezone(tz))
        active = tz;
}
export function activeTimezone() {
    return active;
}
